import { Component, signal, computed, OnInit, inject, effect, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { filter } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { CartService } from '../../../services/cart.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    InputTextModule,
    ButtonModule
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cartService = inject(CartService);
  
  searchQuery = signal('');
  showCart = signal(false); // Only show cart on store pages
  currentPlatformId = signal<string | null>(null); // Store current platform ID
  showCartDropdown = signal(false); // Control cart dropdown visibility
  
  // Cart items for dropdown display
  cartItems = signal<Array<{
    id: string; // Cart item ID (for API calls)
    productId: string; // Product ID (for display)
    name: string;
    price: number;
    quantity: number;
    image?: string;
    totalPrice: number;
  }>>([]);
  cartTotal = signal(0); // Total price of all items

  isAuthenticated = computed(() => this.authService.isAuthenticated());
  user = computed(() => this.authService.getUser());
  cartCount = computed(() => {
    const count = this.cartService.cartCount$();
    return count ?? 0; // Return 0 instead of undefined to avoid change detection issues
  });

  constructor(
    public authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    // Watch for cart count changes to refresh cart items when cart is updated
    // effect() must be called in constructor (injection context)
    let previousCartCount: number | undefined = undefined;
    effect(() => {
      const cartCount = this.cartService.cartCount$();
      // Only reload if cart count actually changed and is not undefined
      if (cartCount !== undefined && cartCount !== previousCartCount) {
        previousCartCount = cartCount;
        // Defer side effects to next tick to avoid change detection errors
        setTimeout(() => {
          if ((this.showCartDropdown() || this.showCart())) {
            const platformId = this.currentPlatformId();
            if (platformId && this.isAuthenticated()) {
              this.loadCartItems();
            }
          }
        }, 0);
      }
    });
  }

  ngOnInit(): void {
    // Monitor route changes to show/hide cart icon
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      const url = event.url;
      // Show cart only on store, checkout, or wishlist pages (not on marketplace)
      const isStorePage = url.startsWith('/store/') || url.startsWith('/checkout/') || url.startsWith('/wishlist/');
      this.showCart.set(isStorePage);
      
      // Extract and store platformId
      if (isStorePage) {
        const match = url.match(/\/(store|checkout|wishlist)\/([^\/]+)/);
        if (match && match[2]) {
          const platformId = match[2];
          this.currentPlatformId.set(platformId);
          
          // Initialize cart and load items if authenticated
          if (this.authService.isAuthenticated()) {
            this.cartService.initializeCart(platformId);
            // Always load cart items when entering a store page
            this.loadCartItems();
          }
        }
      } else {
        this.currentPlatformId.set(null);
      }
    });
    
    // Check initial route
    const currentUrl = this.router.url;
    const isStorePage = currentUrl.startsWith('/store/') || currentUrl.startsWith('/checkout/') || currentUrl.startsWith('/wishlist/');
    this.showCart.set(isStorePage);
    
    // Extract and store platformId from initial route
    if (isStorePage) {
      const match = currentUrl.match(/\/(store|checkout|wishlist)\/([^\/]+)/);
      if (match && match[2]) {
        const platformId = match[2];
        this.currentPlatformId.set(platformId);
        
        // Initialize cart and load items if authenticated
        if (this.authService.isAuthenticated()) {
          this.cartService.initializeCart(platformId);
          // Always load cart items when on a store page
          this.loadCartItems();
        }
      }
    }
  }

  onSearch() {
    if (this.searchQuery().trim()) {
      // TODO: Implement search functionality
      console.log('Searching for:', this.searchQuery());
    }
  }

  onSignIn() {
    this.router.navigate(['/signin']);
  }

  onRegister() {
    this.router.navigate(['/register']);
  }

  onLogout() {
    this.authService.logout();
  }

  onCreateStore() {
    // Open the store creation URL in a new tab
    window.open('https://business.arkifi.store/', '_blank');
  }

  /**
   * Toggle cart dropdown
   */
  toggleCartDropdown(): void {
    const newState = !this.showCartDropdown();
    this.showCartDropdown.set(newState);
    
    // Load cart items when dropdown is opened
    if (newState && this.isAuthenticated()) {
      this.loadCartItems();
    }
  }

  /**
   * Load cart items from API
   */
  private loadCartItems(): void {
    const platformId = this.currentPlatformId();
    if (!platformId || !this.isAuthenticated()) return;

    this.cartService.getCartSummary(platformId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // Convert API cart items to local format
          // item.id is the cart item ID (for API calls), item.productId is the product ID
          const items = response.data.items.map(item => ({
            id: item.id, // Cart item ID (used for update/remove operations)
            productId: item.productId, // Product ID (for display/reference)
            name: item.productName,
            price: item.unitPrice,
            quantity: item.quantity,
            image: item.productImage || undefined,
            totalPrice: item.totalPrice
          }));
          
          this.cartItems.set(items);
          
          // Calculate total
          const total = items.reduce((sum, item) => sum + item.totalPrice, 0);
          this.cartTotal.set(total);
        } else {
          this.cartItems.set([]);
          this.cartTotal.set(0);
        }
      },
      error: (error) => {
        console.error('Failed to load cart items:', error);
        this.cartItems.set([]);
        this.cartTotal.set(0);
      }
    });
  }

  /**
   * Update cart item quantity
   */
  updateCartQuantity(productId: string, quantity: number): void {
    const platformId = this.currentPlatformId();
    if (!platformId || quantity <= 0) {
      this.removeFromCart(productId);
      return;
    }

    // Find cart item from local cart items (which has the cart item ID stored)
    const cartItem = this.cartItems().find(item => item.productId === productId);
    if (cartItem) {
      this.cartService.updateCartItem(
        { cartItemId: cartItem.id, quantity: quantity },
        platformId
      ).subscribe({
        next: () => {
          this.loadCartItems(); // Reload cart
        },
        error: (error) => {
          console.error('Failed to update cart quantity:', error);
        }
      });
    }
  }

  /**
   * Remove item from cart
   */
  removeFromCart(productId: string): void {
    const platformId = this.currentPlatformId();
    if (!platformId) return;

    // Find cart item from local cart items (which has the cart item ID stored)
    const cartItem = this.cartItems().find(item => item.productId === productId);
    if (cartItem) {
      this.cartService.removeFromCart(
        { cartItemId: cartItem.id },
        platformId
      ).subscribe({
        next: () => {
          this.loadCartItems(); // Reload cart
        },
        error: (error) => {
          console.error('Failed to remove item from cart:', error);
        }
      });
    }
  }

  /**
   * Clear entire cart
   */
  clearCart(): void {
    const platformId = this.currentPlatformId();
    if (!platformId) return;

    this.cartService.clearCart(platformId).subscribe({
      next: () => {
        this.loadCartItems(); // Reload cart (will be empty)
        this.showCartDropdown.set(false); // Close dropdown
      },
      error: (error) => {
        console.error('Failed to clear cart:', error);
      }
    });
  }

  /**
   * Navigate to checkout page
   */
  goToCheckout(): void {
    const platformId = this.currentPlatformId();
    if (platformId) {
      this.showCartDropdown.set(false);
      this.router.navigate(['/checkout', platformId]);
    }
  }

  /**
   * Navigate to profile page
   */
  goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  toggleMobileMenu(): void {
    // TODO: Implement mobile menu toggle
    console.log('Toggle mobile menu');
  }
}